import { useState, useEffect } from "react";
import { tauriGetYouTubeInfo, tauriDownloadYouTubeAudio } from "../lib/tauri";
import {
  getUploadUrl,
  uploadFile,
  createReference,
} from "../lib/api";
import { WaveformTrimmer } from "./WaveformTrimmer";
import { trimAudio } from "../lib/audioUtils";

interface TauriYouTubeImportProps {
  onComplete: () => void;
  onCancel: () => void;
}

interface VideoInfo {
  videoId: string;
  title: string;
  thumbnail: string;
  duration: number;
}

/** Parse "90", "1:30", "01:30", "1:02:03" → seconds. Empty → null. Invalid → null. */
function parseTimestamp(input: string): number | null {
  const s = input.trim();
  if (!s) return null;
  const parts = s.split(":");
  if (parts.some((p) => p === "" || !/^\d+(\.\d+)?$/.test(p))) return null;
  const nums = parts.map(Number);
  if (nums.some((n) => Number.isNaN(n) || n < 0)) return null;
  let total = 0;
  if (nums.length === 1) total = nums[0];
  else if (nums.length === 2) total = nums[0] * 60 + nums[1];
  else if (nums.length === 3) total = nums[0] * 3600 + nums[1] * 60 + nums[2];
  else return null;
  return total;
}

function formatDuration(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

export function TauriYouTubeImport({ onComplete, onCancel }: TauriYouTubeImportProps) {
  const [step, setStep] = useState<1 | 2>(1);
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Step 2 state
  const [videoInfo, setVideoInfo] = useState<VideoInfo | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [audioDuration, setAudioDuration] = useState<number>(0);
  const [downloading, setDownloading] = useState(false);
  const [downloadStatus, setDownloadStatus] = useState<string | null>(null);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [trimRange, setTrimRange] = useState<{ start: number; end: number } | null>(null);
  const [saving, setSaving] = useState(false);
  const [startInput, setStartInput] = useState("");
  const [endInput, setEndInput] = useState("");

  // Step 1: Load video info using yt-dlp locally
  const handleLoad = async () => {
    if (!url.trim()) return;
    setLoading(true);
    setError(null);

    try {
      const info = await tauriGetYouTubeInfo(url.trim());
      setVideoInfo(info);
      setStep(2);
    } catch (err: unknown) {
      setError(typeof err === "string" ? err : err instanceof Error ? err.message : "Failed to load video info");
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleLoad();
    }
  };

  // Step 2: Manual download (user clicks Download after optionally entering a range)
  const handleDownload = async () => {
    if (!videoInfo || downloading) return;

    // Parse + validate range inputs
    const startParsed = parseTimestamp(startInput);
    const endParsed = parseTimestamp(endInput);
    if (startInput.trim() && startParsed === null) {
      setError("Start time is invalid. Use seconds (90) or mm:ss (1:30).");
      return;
    }
    if (endInput.trim() && endParsed === null) {
      setError("End time is invalid. Use seconds (90) or mm:ss (1:30).");
      return;
    }
    const start = startParsed ?? 0;
    const end = endParsed ?? videoInfo.duration;
    if (start < 0 || end > videoInfo.duration + 0.5 || start >= end) {
      setError(
        `Invalid range. Must be 0 ≤ start < end ≤ ${formatDuration(videoInfo.duration)}.`
      );
      return;
    }

    const hasRange = startInput.trim() !== "" || endInput.trim() !== "";

    setDownloading(true);
    setDownloadStatus(hasRange ? "Downloading selected range..." : "Downloading audio...");
    setError(null);

    try {
      const result = await tauriDownloadYouTubeAudio(
        url.trim(),
        hasRange ? { startSec: start, endSec: end } : undefined
      );

      // Create a blob URL for the waveform trimmer
      const blob = new Blob([result.data.buffer as ArrayBuffer], { type: "audio/wav" });
      const blobUrl = URL.createObjectURL(blob);

      setAudioBlob(blob);
      setAudioUrl(blobUrl);
      setAudioDuration(result.duration);
      setDownloadStatus(null);
    } catch (err: unknown) {
      setError(typeof err === "string" ? err : err instanceof Error ? err.message : "Failed to download audio");
    } finally {
      setDownloading(false);
    }
  };

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => {
      if (audioUrl) URL.revokeObjectURL(audioUrl);
    };
  }, [audioUrl]);

  const handleTrimUpdate = (start: number, end: number) => {
    setTrimRange({ start, end });
  };

  const handleBack = () => {
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setStep(1);
    setVideoInfo(null);
    setAudioUrl(null);
    setAudioBlob(null);
    setAudioDuration(0);
    setDownloading(false);
    setDownloadStatus(null);
    setTrimRange(null);
    setError(null);
    setStartInput("");
    setEndInput("");
  };

  const handleSave = async () => {
    if (!videoInfo || !audioUrl || !audioBlob) return;
    setSaving(true);
    setError(null);

    try {
      let uploadBlob: Blob = audioBlob;
      let finalDuration = audioDuration;
      let contentType = "audio/wav";

      // Check if trim is meaningful
      const hasTrim =
        trimRange &&
        (trimRange.start > 0.05 ||
          Math.abs(trimRange.end - audioDuration) > 0.05);

      if (hasTrim && trimRange) {
        // Trim client-side
        const trimmedBlob = await trimAudio(audioUrl, trimRange.start, trimRange.end);
        uploadBlob = trimmedBlob;
        finalDuration = trimRange.end - trimRange.start;
      }

      // Upload to S3
      const filename = `${videoInfo.title}.wav`;
      const { uploadUrl, key } = await getUploadUrl("reference", contentType, filename);
      await uploadFile(uploadUrl, uploadBlob, contentType);

      // Create reference with YouTube thumbnail
      await createReference({
        name: videoInfo.title,
        s3Key: key,
        duration: finalDuration,
        imageUrl: videoInfo.thumbnail,
      });

      onComplete();
    } catch (err) {
      setError(typeof err === "string" ? err : err instanceof Error ? err.message : "Failed to save reference");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-medium text-slate-200">
            Import from YouTube
          </h3>
          <span className="text-xs px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-400">
            Desktop
          </span>
        </div>
        <button
          onClick={onCancel}
          className="text-slate-500 hover:text-slate-300 text-sm"
        >
          ✕
        </button>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
          <p className="text-xs text-red-400">{error}</p>
        </div>
      )}

      {/* Step 1: URL Input */}
      {step === 1 && (
        <div className="space-y-3">
          <div className="flex gap-2">
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Paste YouTube URL..."
              autoFocus
              className="flex-1 text-sm text-slate-200 bg-slate-800 border border-slate-600
                rounded-lg px-3 py-2.5 outline-none focus:border-indigo-500 transition-colors"
              disabled={loading}
            />
            <button
              onClick={handleLoad}
              disabled={loading || !url.trim()}
              className="btn-primary text-sm px-4 py-2.5 disabled:opacity-50 whitespace-nowrap"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Loading
                </span>
              ) : (
                "Load"
              )}
            </button>
          </div>
          <p className="text-xs text-slate-500">
            Downloads directly on your machine — no server-side restrictions ✨
          </p>
        </div>
      )}

      {/* Step 2: Preview & Trim */}
      {step === 2 && videoInfo && (
        <div className="space-y-4">
          {/* Video title */}
          <p className="text-sm font-medium text-slate-300 truncate" title={videoInfo.title}>
            {videoInfo.title}
          </p>

          {/* Video thumbnail (embeds don't work in desktop webview) */}
          {videoInfo.thumbnail && (
            <div className="relative w-full rounded-lg overflow-hidden bg-slate-800">
              <img
                src={videoInfo.thumbnail}
                alt={videoInfo.title}
                className="w-full rounded-lg"
              />
              <div className="absolute bottom-2 right-2 bg-black/70 text-xs text-slate-300 px-2 py-0.5 rounded">
                {Math.floor(videoInfo.duration / 60)}:{String(Math.floor(videoInfo.duration % 60)).padStart(2, "0")}
              </div>
            </div>
          )}

          {/* Range inputs + Download button (shown before download completes) */}
          {!audioUrl && !downloading && (
            <div className="space-y-2">
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <label className="block text-xs text-slate-400 mb-1">Start (optional)</label>
                  <input
                    type="text"
                    value={startInput}
                    onChange={(e) => setStartInput(e.target.value)}
                    placeholder="0:00"
                    className="w-full text-sm text-slate-200 bg-slate-800 border border-slate-600
                      rounded-lg px-3 py-2 outline-none focus:border-indigo-500 transition-colors"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-xs text-slate-400 mb-1">End (optional)</label>
                  <input
                    type="text"
                    value={endInput}
                    onChange={(e) => setEndInput(e.target.value)}
                    placeholder={formatDuration(videoInfo.duration)}
                    className="w-full text-sm text-slate-200 bg-slate-800 border border-slate-600
                      rounded-lg px-3 py-2 outline-none focus:border-indigo-500 transition-colors"
                  />
                </div>
                <button
                  onClick={handleDownload}
                  className="btn-primary text-sm px-4 py-2 whitespace-nowrap"
                >
                  Download
                </button>
              </div>
              <p className="text-xs text-slate-500">
                Leave blank to download the full video. Use seconds (90) or mm:ss (1:30) — only the selected range will be downloaded.
              </p>
            </div>
          )}

          {/* Download status */}
          {downloading && (
            <div className="flex items-center gap-3 py-3">
              <div className="flex gap-1">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" style={{ animationDelay: "0.2s" }} />
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" style={{ animationDelay: "0.4s" }} />
              </div>
              <span className="text-sm text-slate-400">
                {downloadStatus || "Downloading..."}
              </span>
            </div>
          )}

          {/* Waveform trimmer */}
          {audioUrl && (
            <div className="space-y-2">
              <WaveformTrimmer audioUrl={audioUrl} duration={audioDuration} onTrim={handleTrimUpdate} />
              <p className="text-xs text-slate-500">
                {trimRange
                  ? `Selected: ${(trimRange.end - trimRange.start).toFixed(1)}s of ${Math.round(audioDuration)}s`
                  : `Full duration: ${Math.round(audioDuration)}s — drag handles to trim`}
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2 justify-end pt-1">
            <button
              onClick={handleBack}
              className="btn-ghost text-sm px-4 py-2"
              disabled={saving}
            >
              Back
            </button>
            <button
              onClick={handleSave}
              disabled={!audioUrl || saving}
              className="btn-primary text-sm px-4 py-2 disabled:opacity-50"
            >
              {saving ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Saving
                </span>
              ) : (
                "Save"
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
